/**
 * Lead Intelligence System Settings — labels/defaults for admin UI (ADR-009).
 * Shown even when DB rows are missing (legacy envs that haven't re-seeded).
 * @see backend/scripts/migration/leadIntelligenceSettingsSeed.js
 * @see docs/lead-intelligence/enablement.md
 */

/**
 * @typedef {object} LeadIntelligenceFieldMeta
 * @property {string} label
 * @property {"leads"} category
 * @property {"rollout"|"probability"|"tuning"} section
 * @property {"boolean"|"string"|"number"} type
 * @property {string} description
 * @property {*} defaultValue
 * @property {object} [metadata]
 */

/** @type {readonly { key: string, title: string, hint?: string }[]} */
export const LEAD_INTELLIGENCE_SECTIONS = Object.freeze([
  {
    key: "rollout",
    title: "Lead Intelligence rollout",
    hint: "Enable in order: master → optional backfill → optional AI narrative → widgets. LLM credentials stay in ENV (AI_LLM_*), never here.",
  },
  {
    key: "probability",
    title: "Conversion probability",
    hint: "Deterministic prior and factor weights for conversion probability (must remain coherent as a model).",
  },
  {
    key: "tuning",
    title: "Refresh tuning",
    hint: "Debounce and related ops knobs for report regeneration.",
  },
]);

/** @type {Record<string, LeadIntelligenceFieldMeta>} */
export const LEAD_INTELLIGENCE_FIELD_META = Object.freeze({
  "leads.intelligence.enabled": {
    label: "Lead Intelligence enabled",
    category: "leads",
    section: "rollout",
    type: "boolean",
    defaultValue: false,
    description:
      "Master enable for Lead Intelligence report generation (deterministic layer). Turn this on first.",
  },
  "leads.intelligence.widgets.enabled": {
    label: "Lead Intelligence widgets enabled",
    category: "leads",
    section: "rollout",
    type: "boolean",
    defaultValue: false,
    description:
      "Show Lead Pulse / Leads intelligence overview widgets when reports are available. Requires master enable.",
  },
  "leads.intelligence.backfill.enabled": {
    label: "Lead Intelligence backfill enabled",
    category: "leads",
    section: "rollout",
    type: "boolean",
    defaultValue: false,
    description:
      "Allow active-lead Lead Intelligence backfill (script or POST /leads/intelligence/backfill).",
  },
  "leads.intelligence.aiEnrichment.enabled": {
    label: "AI enrichment enabled",
    category: "leads",
    section: "rollout",
    type: "boolean",
    defaultValue: false,
    description:
      "Enable structured AI enrichment for Lead Intelligence reports (narrative only). Requires AI_LLM_* ENV.",
  },
  "leads.intelligence.probability.policyVersion": {
    label: "Probability policy version",
    category: "leads",
    section: "probability",
    type: "string",
    defaultValue: "v1",
    description: "Conversion probability policy version label stored on reports.",
  },
  "leads.intelligence.probability.base": {
    label: "Base conversion probability",
    category: "leads",
    section: "probability",
    type: "number",
    defaultValue: 0.35,
    description: "Base conversion probability prior (0–1).",
    metadata: { min: 0, max: 1, step: 0.01 },
  },
  "leads.intelligence.probability.weight.completeness": {
    label: "Weight: completeness",
    category: "leads",
    section: "probability",
    type: "number",
    defaultValue: 0.25,
    description: "Probability weight for completeness factor.",
    metadata: { min: 0, max: 1, step: 0.01 },
  },
  "leads.intelligence.probability.weight.quality": {
    label: "Weight: quality",
    category: "leads",
    section: "probability",
    type: "number",
    defaultValue: 0.25,
    description: "Probability weight for quality score factor.",
    metadata: { min: 0, max: 1, step: 0.01 },
  },
  "leads.intelligence.probability.weight.stage": {
    label: "Weight: lifecycle stage",
    category: "leads",
    section: "probability",
    type: "number",
    defaultValue: 0.2,
    description: "Probability weight for lifecycle stage factor.",
    metadata: { min: 0, max: 1, step: 0.01 },
  },
  "leads.intelligence.probability.weight.age": {
    label: "Weight: lead age",
    category: "leads",
    section: "probability",
    type: "number",
    defaultValue: 0.15,
    description: "Probability weight for lead age factor.",
    metadata: { min: 0, max: 1, step: 0.01 },
  },
  "leads.intelligence.probability.weight.engagement": {
    label: "Weight: engagement",
    category: "leads",
    section: "probability",
    type: "number",
    defaultValue: 0.15,
    description: "Probability weight for engagement factor.",
    metadata: { min: 0, max: 1, step: 0.01 },
  },
  "leads.intelligence.refresh.debounceMs": {
    label: "Refresh debounce (ms)",
    category: "leads",
    section: "tuning",
    type: "number",
    defaultValue: 15000,
    description:
      "Debounce window (ms) for pricing-pipeline fan-in before regenerating reports.",
    metadata: { min: 0, max: 300000, unit: "ms" },
  },
});

/** @type {readonly string[]} */
export const LEAD_INTELLIGENCE_SETTING_KEYS = Object.freeze(
  Object.keys(LEAD_INTELLIGENCE_FIELD_META)
);

/**
 * @param {string} key
 * @returns {LeadIntelligenceFieldMeta|undefined}
 */
export const getLeadIntelligenceFieldMeta = (key) =>
  LEAD_INTELLIGENCE_FIELD_META[key];

/**
 * @param {string} key
 * @returns {boolean}
 */
export const isLeadIntelligenceSettingKey = (key) =>
  typeof key === "string" && key.startsWith("leads.intelligence.");

/**
 * Synthetic setting rows for keys missing from the API response.
 * @param {Array<{key: string}>} existingSettings
 * @returns {Array<object>}
 */
export const getMissingLeadIntelligenceSettings = (existingSettings = []) => {
  const existing = new Set(existingSettings.map((s) => s.key));
  return LEAD_INTELLIGENCE_SETTING_KEYS.filter((key) => !existing.has(key)).map(
    (key) => {
      const meta = LEAD_INTELLIGENCE_FIELD_META[key];
      return {
        key,
        value: meta.defaultValue,
        type: meta.type,
        scope: "private",
        category: meta.category,
        description: meta.description,
        metadata: meta.metadata || {},
        _synthetic: true,
      };
    }
  );
};
